/**
 * Globo thumbnail fallback — search autocomplete + app-owned grids only.
 *
 * The Globo Smart Filter app renders the search dropdown, the /search grid (filterOnSearchPage) and
 * the collection grid (filter.id) from its OWN cached product index. That index still serves the CDN
 * filenames the catalogue had before the 2026-07-29 media rename, so those URLs 404. Measured
 * 2026-08-05: all three results for "FE" returned HTTP 404 from the app while Shopify's live product
 * data returned 200 for the renamed files. Server-rendered Liquid is unaffected, which is exactly
 * why the bug looks search-specific.
 *
 * The durable cure is the app's index (see toolkit/scripts/globo-reindex.mjs) — and, better still,
 * populating the app's own `window.GloboFilterSFAT`, which switches on its built-in Storefront API
 * image refresh and would make it immune to renames. This file is the storefront safety net for the
 * window in between, and for the next time media are renamed.
 *
 * A previous attempt at this (assets/mex-image-repair.js, 6b2eb20) broke every live product page and
 * was reverted in f3300dd: its fallback selector list included `[data-product-id]`, which
 * layout/theme.liquid puts on <body> for the product template, so `img.closest(ITEM)` resolved to
 * <body> and every logo and locale flag on a PDP was repainted as the product photo at 2000x2000.
 * Four structural guards make that impossible here:
 *
 *   1. TEMPLATE GATE. Loaded only on search + collection (snippets/custom-code-body.liquid). The PDP
 *      never downloads this file at all.
 *   2. ERROR-ONLY. Nothing runs until an <img> actually fails to load. An image that renders is
 *      never touched — no MutationObserver, no sweep, no proactive rewrite.
 *   3. CONTAINER SCOPE. The failing image must sit inside a Globo-rendered container.
 *   4. CARD ALLOWLIST. `li` and `[data-product-id]` are deliberately absent from CARD, and the
 *      resolved card is rejected outright if it is <body> or <html>.
 *
 * It writes `src`/`data-src` only. It never writes width or height.
 */
(() => {
  if (window.__mexGloboImageFallback) return;
  window.__mexGloboImageFallback = true;

  /** Containers the filter app renders into. Nothing outside these is ever considered. */
  const APP_CONTAINERS = [
    "#gf-products",
    "#gf-grid",
    "#glFilter-search-popup",
    ".glFilter-search-popup",
    "ul.ui-autocomplete",
    "[data-globo-filter-items]",
  ].join(",");

  /**
   * Card elements that own exactly one product link. `li` and `[data-product-id]` are deliberately
   * NOT here — those two are what resolved to <body> on a PDP in f3300dd.
   */
  const CARD = [
    ".gl-card",
    ".gf-search-item-product",
    ".gf-product-card",
    ".gf-search-item",
    ".m-product-item",
    ".m-product-card",
  ].join(",");

  const THUMB_MAX_PX = 120; // above this the card size is worth the extra bytes

  let map = null;
  const mapReady = () => {
    if (map !== null) return map;
    const node = document.getElementById("mex-globo-image-map");
    try {
      const parsed = node ? JSON.parse(node.textContent) : null;
      map = parsed && parsed.byId ? parsed : { byId: {}, byHandle: {} };
    } catch (e) {
      map = { byId: {}, byHandle: {} };
    }
    return map;
  };

  const fetched = new Map(); // handle -> Promise<{t,c}|null>, so a miss costs one request per product

  /** `/en/products/foo?variant=1` -> `foo`. Returns "" when the href is not a product URL. */
  const handleFromHref = (href) => {
    if (!href) return "";
    const path = href.split("?")[0].split("#")[0];
    const match = path.match(/\/products\/([^/]+)\/?$/);
    return match ? match[1] : "";
  };

  const fromMap = (id, handle) => {
    const m = mapReady();
    if (id && m.byId[id]) return m.byId[id];
    const viaHandle = handle && m.byHandle[handle];
    if (viaHandle && m.byId[viaHandle]) return m.byId[viaHandle];
    return null;
  };

  /** Last resort for a product outside Liquid's 50-item map. One request, cached, after a failure. */
  const fromProductJson = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (fetched.has(handle)) return fetched.get(handle);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const req = fetch(`${root}products/${handle}.js`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const src = data && data.featured_image;
        if (!src) return null;
        const base = src.startsWith("//") ? `https:${src}` : src;
        const join = base.includes("?") ? "&" : "?";
        return { t: `${base}${join}width=300`, c: `${base}${join}width=800` };
      })
      .catch(() => null);
    fetched.set(handle, req);
    return req;
  };

  /**
   * Swap in the live URL. `data-src`/`data-srcset` are handled because the app's thumbnails are
   * lazysizes-managed and would otherwise restore the 404 on the next pass. width/height are never
   * written — that is what let the reverted script resize the header wordmark.
   */
  const paint = (img, entry) => {
    // Measure the BOX, not the element's width attribute — the app ships the source width there
    // (2000), which would pick the card size for a 50px dropdown thumb. A broken image can measure 0,
    // so fall back to the card and then to the larger file: slightly heavy beats visibly blurry.
    const box =
      img.getBoundingClientRect().width ||
      (img.parentElement ? img.parentElement.getBoundingClientRect().width : 0);
    const url = box > 0 && box <= THUMB_MAX_PX ? entry.t : entry.c;
    img.removeAttribute("srcset");
    img.removeAttribute("data-srcset");
    img.setAttribute("data-src", url);
    img.setAttribute("src", url);
    window.__mexGloboImageRepairs = (window.__mexGloboImageRepairs || 0) + 1;
  };

  const onError = (event) => {
    const img = event.target;
    if (!(img instanceof HTMLImageElement)) return;
    if (img.dataset.mexRepaired === "1") return; // the repaired URL failed too — do not loop
    if (!img.closest(APP_CONTAINERS)) return;

    const card = img.closest(CARD);
    if (!card || card === document.body || card === document.documentElement) return;

    const idNode = card.querySelector("[data-product-id]");
    const id = idNode ? idNode.getAttribute("data-product-id") : "";
    const link = card.querySelector('a[href*="/products/"]');
    const handle = handleFromHref(link && link.getAttribute("href"));
    if (!id && !handle) return;

    img.dataset.mexRepaired = "1";

    const entry = fromMap(id, handle);
    if (entry) {
      paint(img, entry);
      return;
    }
    fromProductJson(handle).then((late) => {
      if (late) paint(img, late);
    });
  };

  // Capture phase: `error` does not bubble from <img>, but it does propagate down.
  document.addEventListener("error", onError, true);
})();
